import { useCallback, useEffect, useRef, useState } from 'react';
import quoteService from '../../../services/quote.service';

interface Args {
  /** Rate-zone code — keys the origin-ZIP map server-side. */
  ratezone: string;
  /** Delivery zone (LOCAL / OTR) — selects the origin-ZIP map. */
  deliveryzone: string;
  zip: string;
  city: string;
  state: string;
  /** Master switch — false in read-only contexts (no geocode, no miles). */
  enabled: boolean;
  /** True when the address is location-linked: skip zip↔city/state autofill so
   *  the linked address isn't clobbered, but still compute miles. */
  lockAddress: boolean;
  /** Fill city + state (resolved from a ZIP). */
  onCityState: (city: string, state: string) => void;
  /** Fill ZIP (resolved from city + state). */
  onZip: (zip: string) => void;
  /** Set the computed miles (string, for the text field). */
  onMiles: (miles: string) => void;
  /** Set the dray rate (string) auto-filled from deliverystandards / mileage
   *  bands. Optional — when omitted, no dray-rate lookup runs. */
  onDrayRate?: (rate: string) => void;
  /** The current computed delivery miles (string) — fed to the mileage-band
   *  dray-rate path so a re-computed miles re-prices a mileage zone. */
  miles?: string;
  /** Called when a LOCAL standards zone has no (zone,zip) pairing — the consumer
   *  shows the "Delivery rate is not defined for … to …." modal. */
  onNotDefined?: (message: string) => void;
}

export interface QuoteDeliveryGeo {
  /** Attach to the ZIP field's onBlur — resolves city/state from the ZIP. */
  handleZipBlur: () => void;
  /** Attach to BOTH the city and state fields' onBlur — resolves the ZIP. */
  handleCityStateBlur: () => void;
  /** A geocode or miles request is in flight. */
  geoBusy: boolean;
  /** Last miles message (e.g. "No origin ZIP for this rate zone"), else null. */
  milesError: string | null;
  /** Dray-rate reference for the current (rate-zone, ZIP, miles) — null until a
   *  match is fetched. Drives the reference shown next to the Drayage Rate:
   *  deliverystandards → Standard/Minimum; ratezonemileage PER MILE → Per Mile
   *  Standard (+ a calculated per-mile vs. the typed rate). */
  drayInfo: {
    source: string | null;
    standardRate: number | null;
    minimumRate: number | null;
    perMileStandard: number | null;
    ratetype: string | null;
  } | null;
}

/**
 * Delivery-location geocoding + mileage for the quote forms (V1
 * inline_quote.pl getcitystatezip + calcmiles), shared by the Create drawer
 * (StepDelivery) and the editor (DirectTab):
 *  - leave the ZIP field  → fill city/state (Google Geocoding)
 *  - leave city + state   → fill ZIP
 *  - changing the ZIP/city/state OR rate-zone/delivery-zone → recompute miles
 *    (rate-zone origin ZIP → delivery, Google Distance Matrix)
 *
 * The miles recalc is debounced and skips the first run, so opening an existing
 * quote never silently overwrites its saved miles — only a real edit does.
 */
export default function useQuoteDeliveryGeo(a: Args): QuoteDeliveryGeo {
  const { ratezone, deliveryzone, zip, city, state, enabled, lockAddress, miles } = a;

  // Latest callbacks held in a ref so they don't re-trigger the miles effect.
  const cbs = useRef({ onCityState: a.onCityState, onZip: a.onZip, onMiles: a.onMiles, onDrayRate: a.onDrayRate, onNotDefined: a.onNotDefined });
  cbs.current = { onCityState: a.onCityState, onZip: a.onZip, onMiles: a.onMiles, onDrayRate: a.onDrayRate, onNotDefined: a.onNotDefined };

  const [geoBusy, setGeoBusy] = useState(false);
  const [milesError, setMilesError] = useState<string | null>(null);
  const [drayInfo, setDrayInfo] = useState<QuoteDeliveryGeo['drayInfo']>(null);

  const lastGeoZip = useRef<string>('');
  const lastGeoCS = useRef<string>('');

  const handleZipBlur = useCallback(() => {
    if (!enabled || lockAddress) return;
    const z = (zip || '').trim().slice(0, 5);
    if (z.length < 5 || z === lastGeoZip.current) return;
    lastGeoZip.current = z;
    setGeoBusy(true);
    quoteService.geocodeCityStateZip(z)
      .then((r) => { if (r && (r.city || r.state)) cbs.current.onCityState(r.city, r.state); })
      .catch(() => { /* non-fatal — user can type manually */ })
      .finally(() => setGeoBusy(false));
  }, [enabled, lockAddress, zip]);

  const handleCityStateBlur = useCallback(() => {
    if (!enabled || lockAddress) return;
    const c = (city || '').trim();
    const s = (state || '').trim();
    if (!c || !s) return;
    const key = `${c}, ${s}`.toUpperCase();
    if (key === lastGeoCS.current) return;
    lastGeoCS.current = key;
    setGeoBusy(true);
    quoteService.geocodeCityStateZip(`${c}, ${s}`)
      .then((r) => { if (r && r.zip) cbs.current.onZip(r.zip); })
      .catch(() => { /* non-fatal */ })
      .finally(() => setGeoBusy(false));
  }, [enabled, lockAddress, city, state]);

  // Debounced miles recalc. First run is skipped (mount) so editing an existing
  // quote doesn't overwrite its saved miles; later changes to the rate zone,
  // delivery zone, or destination recompute. Successful results set miles; an
  // error (no origin ZIP / no route) surfaces a notice and leaves miles as-is.
  // initialKey snapshots the inputs as first loaded; we never recompute while
  // the inputs still equal that snapshot, so opening a quote can't auto-overwrite
  // its saved miles (which would read as unsaved changes). Keying off the value
  // — not a one-shot boolean — survives React StrictMode's double-invoke of the
  // effect AND an `enabled` false→true flip. Only a genuine edit recomputes.
  const initialKey = useRef<string | null>(null);
  const lastMilesKey = useRef<string>('');
  useEffect(() => {
    if (!enabled) return;

    const z = (zip || '').trim().slice(0, 5);
    const c = (city || '').trim();
    const s = (state || '').trim();
    const key = [ratezone, deliveryzone, z, c, s].join('|').toUpperCase();

    if (initialKey.current === null) { initialKey.current = key; return; }
    if (key === initialKey.current) return; // unchanged from load (incl. StrictMode re-invoke)

    const hasDest = z.length === 5 || (!!c && !!s);
    if (!ratezone || !hasDest) { setMilesError(null); return; }
    if (key === lastMilesKey.current) return;

    const t = window.setTimeout(() => {
      lastMilesKey.current = key;
      setGeoBusy(true);
      quoteService.calculateQuoteMiles({
        ratezone,
        deliveryzone,
        zip: z || undefined,
        city: c || undefined,
        state: s || undefined,
      })
        .then((res) => {
          setMilesError(res.error ?? null);
          if (res.miles != null) cbs.current.onMiles(String(res.miles));
        })
        .catch(() => setMilesError('Could not calculate miles.'))
        .finally(() => setGeoBusy(false));
    }, 600);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, ratezone, deliveryzone, zip, city, state]);

  // Debounced Dray Rate auto-fill (V1 updatedirectdelivery): a deliverystandards
  // (zone,zip) standard rate, a ratezonemileage band, or a "not defined" modal.
  // Keyed on (rate-zone, ZIP, delivery-zone, miles): the miles change re-prices a
  // mileage zone after the distance recompute. First run is skipped (mount) so
  // opening a quote never overwrites its saved dray rate; only a real edit fetches.
  // Silent on error — leaves the rate / reference as-is.
  const drayInitialKey = useRef<string | null>(null);
  const lastDrayKey = useRef<string>('');
  useEffect(() => {
    if (!enabled || !cbs.current.onDrayRate) return;
    const z = (zip || '').trim().slice(0, 5);
    const m = parseFloat(miles || '') || 0;
    const key = [ratezone, z, deliveryzone, m].join('|').toUpperCase();

    if (drayInitialKey.current === null) { drayInitialKey.current = key; return; }
    if (key === drayInitialKey.current) return;
    if (!ratezone || z.length !== 5) return;
    if (key === lastDrayKey.current) return;

    const t = window.setTimeout(() => {
      lastDrayKey.current = key;
      quoteService.getDrayRate({ ratezone, zip: z, deliveryzone, miles: m })
        .then((res) => {
          setDrayInfo({
            source: res.source,
            standardRate: res.standardRate,
            minimumRate: res.minimumRate,
            perMileStandard: res.perMileStandard,
            ratetype: res.ratetype,
          });
          if (res.notDefined) {
            cbs.current.onNotDefined?.(res.message || `Delivery rate is not defined for ${ratezone} to ${z}.`);
            return;
          }
          if (res.drayRate != null) cbs.current.onDrayRate?.(String(res.drayRate));
        })
        .catch(() => { /* non-fatal — leave the dray rate / reference as-is */ });
    }, 600);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, ratezone, zip, deliveryzone, miles]);

  // Dray-rate REFERENCE (Standard / Minimum / Per-Mile) for the current
  // destination. Unlike the auto-fill effect above — which skips the first run
  // so it never clobbers a saved rate — this also fetches on load: the
  // reference is read-only, so populating the hint is always safe. That makes
  // the Minimum/Standard hint show when editing an existing quote, not only
  // after the destination changes. Keyed on (rate-zone, ZIP, delivery-zone,
  // miles); the key is committed INSIDE the debounce so StrictMode's
  // double-invoke (and an enabled false→true flip) fetch exactly once per key.
  const lastRefKey = useRef<string>('');
  useEffect(() => {
    if (!enabled || !cbs.current.onDrayRate) return;
    const z = (zip || '').trim().slice(0, 5);
    if (!ratezone || z.length !== 5) return;
    const m = parseFloat(miles || '') || 0;
    const key = [ratezone, z, deliveryzone, m].join('|').toUpperCase();
    if (key === lastRefKey.current) return;
    const t = window.setTimeout(() => {
      lastRefKey.current = key;
      quoteService.getDrayRate({ ratezone, zip: z, deliveryzone, miles: m })
        .then((res) => setDrayInfo({
          source: res.source,
          standardRate: res.standardRate,
          minimumRate: res.minimumRate,
          perMileStandard: res.perMileStandard,
          ratetype: res.ratetype,
        }))
        .catch(() => { /* non-fatal — the hint just won't show */ });
    }, 300);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, ratezone, zip, deliveryzone, miles]);

  return { handleZipBlur, handleCityStateBlur, geoBusy, milesError, drayInfo };
}
